// src/context/AuthContext.jsx
import React, { createContext, useContext, useState, useEffect } from 'react';
import { 
  signInWithEmailAndPassword,
  createUserWithEmailAndPassword,
  signInWithPopup,
  GoogleAuthProvider,
  signOut,
  onAuthStateChanged,
  sendPasswordResetEmail,
  updateProfile,
  updateEmail,
  updatePassword,
  EmailAuthProvider,
  reauthenticateWithCredential
} from 'firebase/auth';
import { doc, getDoc, setDoc, updateDoc, serverTimestamp, runTransaction } from 'firebase/firestore';
import { auth, db } from '../firebase/config';

const AuthContext = createContext({});

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [userProfile, setUserProfile] = useState(null);
  const [organizationProfile, setOrganizationProfile] = useState(null);
  const [loading, setLoading] = useState(false);
  const [initialLoading, setInitialLoading] = useState(true);

  // Initialize Google Auth Provider
  const googleProvider = new GoogleAuthProvider();
  googleProvider.addScope('email');
  googleProvider.addScope('profile');

  // Email/Password Login
  const login = async (email, password) => {
    try {
      setLoading(true);
      const result = await signInWithEmailAndPassword(auth, email, password);
      
      // Sync to Openchat after successful login
      await syncUserToOpenchat(result.user.uid);
      
      return result;
    } catch (error) {
      console.error('Login error:', error);
      throw new Error(getAuthErrorMessage(error.code));
    } finally {
      setLoading(false);
    }
  };

  // Updated register function
  const register = async (email, password, additionalData = {}) => {
    try {
      setLoading(true);
      const result = await createUserWithEmailAndPassword(auth, email, password);
      
      // Update the user's display name if provided
      if (additionalData.displayName) {
        await updateProfile(result.user, {
          displayName: additionalData.displayName
        });
      }

      // Create user profile and organization atomically
      await createUserProfileWithOrganization(result.user, additionalData);
      
      // Sync to Openchat after profile creation
      await syncUserToOpenchat(result.user.uid);
      
      return result;
    } catch (error) {
      console.error('Registration error:', error);
      throw new Error(getAuthErrorMessage(error.code));
    } finally {
      setLoading(false);
    }
  };

  // Updated Google login function
  const loginWithGoogle = async () => {
    try {
      setLoading(true);
      console.log('Starting Google login...');
      
      const result = await signInWithPopup(auth, googleProvider);
      console.log('Google login successful:', result.user.email);
      
      // Check if user profile exists, create if not
      const userDoc = await getDoc(doc(db, 'parrot-users', result.user.uid));
      if (!userDoc.exists()) {
        console.log('Creating new user profile for Google user');
        await createUserProfileWithOrganization(result.user, {
          displayName: result.user.displayName,
          provider: 'google'
        });
      } else {
        console.log('User profile already exists');
      }
      
      // Sync to Openchat after login/profile creation
      await syncUserToOpenchat(result.user.uid);
      
      return result;
    } catch (error) {
      console.error('Google login error:', error);
      throw new Error(getAuthErrorMessage(error.code));
    } finally {
      setLoading(false);
    }
  };

  const syncUserToOpenchat = async (uid) => {
    try {
      // Get latest user data from parrot-users
      const userDocRef = doc(db, 'parrot-users', uid);
      const userDoc = await getDoc(userDocRef);
      
      if (!userDoc.exists()) {
        console.warn('User document not found for sync:', uid);
        return;
      }
      
      const userData = userDoc.data();
      
      // Sync to openchat-users
      const openchatUserRef = doc(db, 'openchat-users', uid);
      await setDoc(openchatUserRef, {
        uid,
        displayName: userData.displayName || userData.email?.split('@')[0],
        email: userData.email,
        profilePhoto: userData.profilePhoto || null,
        phoneNumber: userData.phoneNumber || null,
        role: userData.role || 'user',
        organizationId: userData.organizationId || null,
        isOnline: true,
        syncedFrom: 'parrotoffice',
        lastSyncedAt: serverTimestamp(),
        createdAt: serverTimestamp()
      }, { merge: true });
      
      console.log('User synced to Openchat:', uid);
    } catch (error) {
      console.error('Error syncing user to Openchat:', error);
      // Don't block login if sync fails
    }
  };

  // Create user profile and organization atomically using a transaction
  const createUserProfileWithOrganization = async (user, additionalData = {}) => {
    try {
      console.log('Creating user profile and organization...');
      
      const organizationId = additionalData.organizationId || generateOrganizationId();
      const organizationName = additionalData.organizationName || 'Your Organization';
      
      // Use a transaction to ensure both user and organization are created together
      await runTransaction(db, async (transaction) => {
        const userRef = doc(db, 'parrot-users', user.uid);
        const orgRef = doc(db, 'parrot-organizations', organizationId);
        
        // Check if user already exists
        const userDoc = await transaction.get(userRef);
        if (userDoc.exists()) {
          console.log('User profile already exists, skipping creation');
          return;
        }
        
        // User profile data
        const profileData = {
          uid: user.uid,
          email: user.email,
          displayName: additionalData.displayName || user.displayName || user.email?.split('@')[0] || '',
          organizationId,
          organizationName,
          industry: additionalData.industry || '',
          website: additionalData.website || '',
          phone: additionalData.phone || '',
          jobTitle: additionalData.jobTitle || '',
          primaryColor: additionalData.primaryColor || '#2563eb',
          plan: additionalData.plan || 'free',
          provider: additionalData.provider || 'email',
          profileComplete: false,
          createdAt: new Date().toISOString(),
          updatedAt: new Date().toISOString(),
          settings: {
            notifications: {
              email: true,
              push: true,
              marketing: false
            },
            preferences: {
              theme: 'light',
              language: 'en',
              timezone: Intl.DateTimeFormat().resolvedOptions().timeZone
            }
          }
        };

        // Organization data
        const orgData = {
          id: organizationId,
          name: organizationName,
          industry: additionalData.industry || '',
          website: additionalData.website || '',
          phone: additionalData.phone || '',
          createdAt: new Date().toISOString(),
          updatedAt: new Date().toISOString(),
          createdBy: user.uid,
          members: [user.uid], // User is the first member
          settings: {
            emailSettings: {
              fromName: organizationName,
              replyTo: user.email
            }
          }
        };

        // Create both documents in the transaction
        transaction.set(userRef, profileData);
        transaction.set(orgRef, orgData);
        
        console.log('User profile and organization created successfully');
      });
      
      // Fetch the created profiles
      const userProfile = await fetchUserProfile(user.uid);
      const orgProfile = await fetchOrganizationProfile(organizationId);
      
      setUserProfile(userProfile);
      setOrganizationProfile(orgProfile);
      
      return userProfile;
    } catch (error) {
      console.error('Error creating user profile with organization:', error);
      throw new Error('Failed to create user profile and organization');
    }
  };

  // Generate unique organization ID
  const generateOrganizationId = () => {
    return 'org_' + Math.random().toString(36).substr(2, 9) + Date.now().toString(36);
  };

  // Fetch user profile from Firestore with error handling
  const fetchUserProfile = async (uid) => {
    try {
      console.log('Fetching user profile for:', uid);
      const userDoc = await getDoc(doc(db, 'parrot-users', uid));
      if (userDoc.exists()) {
        const userData = userDoc.data();
        console.log('User profile fetched:', userData.email, 'Org:', userData.organizationId);
        return userData;
      }
      console.log('User profile not found');
      return null;
    } catch (error) {
      console.error('Error fetching user profile:', error);
      if (error.code === 'permission-denied') {
        console.error('Permission denied when fetching user profile');
      }
      return null;
    }
  };

  // Fetch organization profile with better error handling
  const fetchOrganizationProfile = async (orgId) => {
    try {
      if (!orgId) {
        console.log('No organization ID provided');
        return null;
      }
      
      console.log('Fetching organization profile for:', orgId);
      const orgDoc = await getDoc(doc(db, 'parrot-organizations', orgId));
      if (orgDoc.exists()) {
        const orgData = orgDoc.data();
        console.log('Organization profile fetched:', orgData.name);
        return orgData;
      }
      console.log('Organization profile not found');
      return null;
    } catch (error) {
      console.error('Error fetching organization profile:', error);
      if (error.code === 'permission-denied') {
        console.error('Permission denied when fetching organization profile. User may not have access to org:', orgId);
        // Don't throw error, just return null and let app continue
        return null;
      }
      return null;
    }
  };

  // Update user profile
  const updateUserProfile = async (updates) => {
    try {
      if (!user) throw new Error('No user logged in');
      
      const userRef = doc(db, 'parrot-users', user.uid);
      const updateData = {
        ...updates,
        updatedAt: new Date().toISOString()
      };
      
      await updateDoc(userRef, updateData);
      
      // Update local state
      setUserProfile(prev => ({
        ...prev,
        ...updateData
      }));
      
      // Update Firebase Auth profile if display name changed
      if (updates.displayName && updates.displayName !== user.displayName) {
        await updateProfile(user, { displayName: updates.displayName });
      }
      
      return true;
    } catch (error) {
      console.error('Error updating user profile:', error);
      throw new Error('Failed to update profile');
    }
  };

  // Update organization profile
  const updateOrganizationProfile = async (updates) => {
    try {
      if (!user || !userProfile?.organizationId) {
        throw new Error('No organization found');
      }
      
      const orgRef = doc(db, 'parrot-organizations', userProfile.organizationId);
      const updateData = {
        ...updates,
        updatedAt: new Date().toISOString()
      };
      
      await updateDoc(orgRef, updateData);
      
      // Update local state
      setOrganizationProfile(prev => ({
        ...prev,
        ...updateData
      }));
      
      return true;
    } catch (error) {
      console.error('Error updating organization profile:', error);
      throw new Error('Failed to update organization profile');
    }
  };

  // Complete user profile setup
  const completeUserProfile = async (profileData) => {
    try {
      if (!user) throw new Error('No user logged in');
      
      // First update organization profile if we have one
      if (profileData.organizationProfile && userProfile?.organizationId) {
        await updateOrganizationProfile(profileData.organizationProfile);
      }
      
      // Then update user profile with completion flag
      const updatedUserProfile = {
        ...userProfile, // Keep existing profile data
        ...profileData.userProfile, // Add new data
        profileComplete: true, // Mark as complete
        updatedAt: new Date().toISOString()
      };
      
      await updateUserProfile(updatedUserProfile);
   
      return true;
    } catch (error) {
      console.error('Error completing user profile:', error);
      throw error;
    }
  };

  // Other methods remain the same...
  const updateUserEmail = async (newEmail, currentPassword) => {
    try {
      if (!user) throw new Error('No user logged in');
      
      const credential = EmailAuthProvider.credential(user.email, currentPassword);
      await reauthenticateWithCredential(user, credential);
      
      await updateEmail(user, newEmail);
      await updateUserProfile({ email: newEmail });
      
      return true;
    } catch (error) {
      console.error('Error updating email:', error);
      throw new Error(getAuthErrorMessage(error.code));
    }
  };

  const updateUserPassword = async (currentPassword, newPassword) => {
    try {
      if (!user) throw new Error('No user logged in');
      
      const credential = EmailAuthProvider.credential(user.email, currentPassword);
      await reauthenticateWithCredential(user, credential);
      
      await updatePassword(user, newPassword);
      return true;
    } catch (error) {
      console.error('Error updating password:', error);
      throw new Error(getAuthErrorMessage(error.code));
    }
  };

  const resetPassword = async (email) => {
    try {
      await sendPasswordResetEmail(auth, email);
      return true;
    } catch (error) {
      console.error('Password reset error:', error);
      throw new Error(getAuthErrorMessage(error.code));
    }
  };

  const logout = async () => {
    try {
      await signOut(auth);
      setUser(null);
      setUserProfile(null);
      setOrganizationProfile(null);
    } catch (error) {
      console.error('Logout error:', error);
      throw new Error('Failed to logout');
    }
  };

  const deleteAccount = async (password) => {
    try {
      if (!user) throw new Error('No user logged in');
      
      if (userProfile?.provider === 'email') {
        const credential = EmailAuthProvider.credential(user.email, password);
        await reauthenticateWithCredential(user, credential);
      }
      
      await user.delete();
      
      setUser(null);
      setUserProfile(null);
      setOrganizationProfile(null);
      
      return true;
    } catch (error) {
      console.error('Account deletion error:', error);
      throw new Error(getAuthErrorMessage(error.code));
    }
  };

  // Auth error message helper
  const getAuthErrorMessage = (errorCode) => {
    const errorMessages = {
      'auth/user-not-found': 'No account found with this email address.',
      'auth/wrong-password': 'Incorrect password.',
      'auth/email-already-in-use': 'An account with this email already exists.',
      'auth/weak-password': 'Password should be at least 6 characters.',
      'auth/invalid-email': 'Invalid email address.',
      'auth/requires-recent-login': 'Please log in again to perform this action.',
      'auth/too-many-requests': 'Too many failed attempts. Please try again later.',
      'auth/popup-closed-by-user': 'Google sign-in was cancelled.',
      'auth/cancelled-popup-request': 'Google sign-in was cancelled.',
      'auth/popup-blocked': 'Pop-up blocked. Please allow pop-ups and try again.',
      'auth/account-exists-with-different-credential': 'An account already exists with this email but different sign-in method.',
      'auth/invalid-credential': 'The credential is invalid or has expired.',
      'auth/operation-not-allowed': 'This sign-in method is not enabled.',
      'auth/user-disabled': 'This account has been disabled.',
      'auth/user-token-expired': 'Your session has expired. Please sign in again.',
      'auth/network-request-failed': 'Network error. Please check your connection.',
    };
    
    return errorMessages[errorCode] || 'An unexpected error occurred. Please try again.';
  };

  // Monitor auth state changes
  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, async (firebaseUser) => {
      try {
        setInitialLoading(true);
        
        if (firebaseUser) {
          setUser(firebaseUser);
          
          // Fetch user profile from Firestore
          const profile = await fetchUserProfile(firebaseUser.uid);
          setUserProfile(profile);
          
          // If no profile exists, create one (for existing users)
          if (!profile) {
            console.log('No profile found, creating one...');
            const newProfile = await createUserProfileWithOrganization(firebaseUser, {
              displayName: firebaseUser.displayName,
              provider: firebaseUser.providerData[0]?.providerId === 'google.com' ? 'google' : 'email'
            });
            setUserProfile(newProfile);
          }
          
          // Handle redirect after successful authentication
          const redirectTo = sessionStorage.getItem('redirectAfterLogin');
          if (redirectTo && profile?.profileComplete) {
            sessionStorage.removeItem('redirectAfterLogin');
            window.location.href = redirectTo;
          }
        } else {
          setUser(null);
          setUserProfile(null);
          setOrganizationProfile(null);
        }
      } catch (error) {
        console.error('Auth state change error:', error);
      } finally {
        setInitialLoading(false);
        setLoading(false);
      }
    });

    return unsubscribe;
  }, []);

  const value = {
    // User state
    user,
    userProfile,
    organizationProfile,
    loading,
    initialLoading,
    
    // Authentication methods
    login,
    register,
    loginWithGoogle,
    logout,
    resetPassword,
    
    // Profile management
    updateUserProfile,
    updateOrganizationProfile,
    completeUserProfile,
    updateUserEmail,
    updateUserPassword,
    deleteAccount,
    fetchOrganizationProfile,
    
    // Utility
    getAuthErrorMessage
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};